import { routeConfig } from './routes.js'

/**
 * Navigation Graph — route relationships derived from the route registry.
 *
 * Every registered route is a graph node. Route metadata owns labels and
 * outgoing edges, keeping authorization, loading, transitions, breadcrumbs,
 * and predictive prefetch attached to the same route definition.
 */

/**
 * @typedef {import('./types.js').Direction} DirectionalSemantic
 *
 * @typedef {Object} GraphNode
 * @property {string} path
 * @property {string} label
 * @property {import('./types.js').RouteEdge[]} edges
 */

function route_label(config) {
  if (config.meta.title) return config.meta.title
  if (config.path === '/') return 'Landing'

  const segments = config.path.split('/').filter(Boolean)
  const last_segment = segments.at(-1)
  const is_detail = last_segment.startsWith(':')
  const segment = is_detail ? segments.at(-2) : last_segment
  const label = segment
    .replace(/^g$/, 'Grade')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())

  return is_detail ? `${label.replace(/s$/, '')} Detail` : label
}

function path_pattern(path) {
  return new RegExp(`^${path.replace(/:[a-zA-Z_]+/g, '[^/]+')}$`)
}

/** @type {GraphNode[]} */
export const graph = routeConfig.map((config) => ({
  path: config.path,
  label: route_label(config),
  edges: config.meta.edges || [],
}))

/**
 * Find outgoing edges for a route path.
 * @param {string} pathname
 * @returns {import('./types.js').RouteEdge[]}
 */
export function getEdgesForPath(pathname) {
  return getNodeForPath(pathname)?.edges || []
}

/**
 * Look up a graph node by exact path, then route pattern.
 * @param {string} pathname
 * @returns {GraphNode | undefined}
 */
export function getNodeForPath(pathname) {
  return graph.find((node) => node.path === pathname)
    || graph.find((node) => path_pattern(node.path).test(pathname))
}

/**
 * Build a breadcrumb trail by resolving each path prefix against registered
 * routes. Canonical aliases update the current crumb instead of duplicating it.
 * @param {string} pathname
 * @returns {Array<{ label: string, href: string }>}
 */
export function getBreadcrumbs(pathname) {
  if (!pathname) return []

  const trail = []
  const segments = pathname.split('/').filter(Boolean)
  const seed_path = pathname.startsWith('/admin') ? '/admin' : '/'
  const seed_node = getNodeForPath(seed_path)
  if (seed_node) trail.push({ label: seed_node.label, href: seed_path })

  let prefix = seed_path === '/admin' ? '/admin' : ''
  const start_index = seed_path === '/admin' ? 1 : 0

  for (let index = start_index; index < segments.length; index++) {
    prefix += '/' + segments[index]
    const node = getNodeForPath(prefix) || getNodeForPath(prefix + '/')
    if (!node) continue

    const current = trail.at(-1)
    if (current?.label === node.label) {
      current.href = prefix
    } else if (current?.href !== prefix) {
      trail.push({ label: node.label, href: prefix })
    }
  }

  return trail
}

/**
 * Get the declared semantic for a transition, or the destination route's
 * metadata-derived fallback when no explicit relationship exists.
 * @param {string} from_path
 * @param {string} to_path
 * @param {DirectionalSemantic} fallback
 * @returns {DirectionalSemantic}
 */
export function getTransitionSemantic(from_path, to_path, fallback = 'push') {
  for (const edge of getEdgesForPath(from_path)) {
    if (path_pattern(edge.to).test(to_path)) return edge.semantic
  }

  return fallback
}
